import {BUILD_VERSION} from './buildVersion'

const VERSION_URL = `${process.env.PUBLIC_URL || ''}/version.json`
const CHECK_INTERVAL_MS = 5 * 60 * 1000
const RELOAD_GUARD_KEY = 'cultiva:last-reloaded-version'

let checking = false
let reloading = false

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {cache: 'no-store'})
    if (!response.ok) {
      return null
    }
    const data = await response.json()
    return typeof data?.version === 'string' ? data.version : null
  } catch {
    return null
  }
}

async function clearBrowserCaches(): Promise<void> {
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch {
    // cache API indisponível ou bloqueada: segue para o reload mesmo assim
  }

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map((registration) => registration.unregister()))
    }
  } catch {
    // idem
  }
}

/**
 * Evita loop de reload caso o `version.json` e o bundle fiquem dessincronizados
 * (ex.: deploy parcial). Só recarrega uma vez por versão detectada.
 */
function shouldReloadFor(deployedVersion: string): boolean {
  try {
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === deployedVersion) {
      return false
    }
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, deployedVersion)
  } catch {
    // sessionStorage indisponível: aceita o risco e recarrega
  }
  return true
}

async function checkForUpdate(): Promise<void> {
  if (checking || reloading || BUILD_VERSION === 'dev') {
    return
  }
  checking = true
  try {
    const deployedVersion = await fetchDeployedVersion()
    if (!deployedVersion || deployedVersion === BUILD_VERSION) {
      return
    }
    if (!shouldReloadFor(deployedVersion)) {
      return
    }

    reloading = true
    await clearBrowserCaches()
    window.location.reload()
  } finally {
    checking = false
  }
}

/**
 * Detecta novas versões publicadas do frontend e recarrega a página limpando o
 * cache, dispensando o Ctrl+F5 manual do usuário.
 *
 * Verifica na inicialização, periodicamente e ao voltar o foco para a aba.
 */
export function startAppUpdateChecker(): void {
  void checkForUpdate()

  window.setInterval(() => {
    void checkForUpdate()
  }, CHECK_INTERVAL_MS)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate()
    }
  })
}
